import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Plus, Lock, RotateCcw } from "lucide-react";
import ClientShell from "./ClientShell";
import { useMe } from "@/lib/rehearsalRoomQueries";
import {
  useWorkDocumentDetail, useCreateBlock, useSubmitDocument, useReopenDocument,
} from "@/lib/workDocumentQueries";
import { useBlockPresence } from "@/hooks/useBlockPresence";
import BlockCard from "./workdoc/BlockCard";
import WorkDocumentVersionPanel from "./workdoc/WorkDocumentVersionPanel";
import PromptDialog from "./presentation/PromptDialog";
import ConfirmDialog from "./presentation/ConfirmDialog";

export default function WorkDocumentDetail() {
  const { workspaceId, docId } = useParams();
  const navigate = useNavigate();
  const { data: me } = useMe();
  const [addBlockOpen, setAddBlockOpen] = useState(false);
  const [confirmSubmitOpen, setConfirmSubmitOpen] = useState(false);
  const { data, isPending, isError } = useWorkDocumentDetail(workspaceId, docId);
  const createBlock = useCreateBlock(workspaceId, docId);
  const submitDocument = useSubmitDocument(workspaceId, docId);
  const reopenDocument = useReopenDocument(workspaceId, docId);

  useBlockPresence({ workspaceId, docId, enabled: !!data && !isError });

  if (isError) {
    return (
      <ClientShell>
        <div className="max-w-md text-center mx-auto mt-16" data-testid="work-document-detail-not-found">
          <p className="font-serif text-xl text-ink mb-2">Document not found</p>
          <p className="text-ink-soft text-sm mb-4">It may have been removed, or you may not have access to it.</p>
          <button
            onClick={() => navigate(`/rehearsal-room/w/${workspaceId}/work-documents`)}
            className="text-sm font-medium text-terracotta hover:text-terracotta/80"
            data-testid="work-document-detail-not-found-back"
          >
            Back to Work Documents
          </button>
        </div>
      </ClientShell>
    );
  }

  if (isPending || !data) {
    return (
      <ClientShell>
        <p className="text-ink-soft" data-testid="work-document-detail-loading">Loading document…</p>
      </ClientShell>
    );
  }

  const isCoach = me?.portal_role === "coach_admin";
  const document_ = data.document;
  const isSubmitted = document_.status === "submitted";
  const blocks = data.blocks || [];

  return (
    <ClientShell>
      <div className="max-w-3xl" data-testid="work-document-detail-page">
        <div className="flex items-center justify-between gap-3 mb-1 flex-wrap">
          <h1 className="font-serif text-2xl text-ink" data-testid="work-document-detail-title">{document_.title}</h1>
          <div className="flex items-center gap-3">
            {isSubmitted && (
              <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wide text-mustard-dark" data-testid="work-document-detail-submitted-badge">
                <Lock size={12} /> Submitted
              </span>
            )}
            {isSubmitted && isCoach && (
              <button
                onClick={() => reopenDocument.mutate()}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-terracotta hover:text-terracotta/80"
                data-testid="work-document-detail-reopen"
              >
                <RotateCcw size={12} /> Reopen
              </button>
            )}
            {!isSubmitted && (
              <button
                onClick={() => setConfirmSubmitOpen(true)}
                className="text-xs font-medium text-ink-soft hover:text-ink"
                data-testid="work-document-detail-submit"
              >
                Submit
              </button>
            )}
          </div>
        </div>
        <p className="text-xs text-ink-soft mb-5 capitalize">{document_.visibility_scope.replace("_", " ")} · {document_.status}</p>

        <div className="space-y-3" data-testid="work-document-detail-blocks">
          {blocks.map((b) => (
            <BlockCard key={b.block_id} workspaceId={workspaceId} docId={docId} block={b} isViewAsClient={false} isDocumentSubmitted={isSubmitted} />
          ))}
          {blocks.length === 0 && <p className="text-ink-soft text-sm" data-testid="work-document-detail-blocks-empty">No sections yet.</p>}
        </div>

        {!isSubmitted && isCoach && (
          <button
            onClick={() => setAddBlockOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:text-terracotta/80 mt-4"
            data-testid="work-document-detail-add-block"
          >
            <Plus size={14} /> Add a section
          </button>
        )}

        <WorkDocumentVersionPanel
          workspaceId={workspaceId} docId={docId} canEdit={!isSubmitted}
          hasSourceFile={!!document_.source_fas_id} documentTitle={document_.title}
        />
      </div>

      <PromptDialog
        open={addBlockOpen} onOpenChange={setAddBlockOpen} title="Name this section" placeholder="e.g. Weekly Reflection"
        confirmLabel="Add"
        onConfirm={(label) => createBlock.mutate({
          block_key: label.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 60) || "section",
          block_type: "text", content: {}, display_order: blocks.length,
          access_policy: { visibility: "editable", editable_role_allowlist: ["coach", "client_member"] },
        })}
        testId="work-document-add-block-dialog"
      />
      <ConfirmDialog
        open={confirmSubmitOpen} onOpenChange={setConfirmSubmitOpen}
        title="Submit this document?" confirmLabel="Submit"
        description="Once submitted, every section becomes read-only until a coach explicitly reopens it."
        onConfirm={() => { submitDocument.mutate(); setConfirmSubmitOpen(false); }}
        testId="work-document-submit-confirm"
      />
    </ClientShell>
  );
}
